import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ZipReader, BlobReader, BlobWriter } from "@zip.js/zip.js";
import {
  Upload, Image as ImageIcon, QrCode, CheckSquare, ExternalLink, CalendarDays, MapPin,
  Loader2, CheckCircle2, XCircle, MessageSquare, Bell, Heart, ArrowLeft,
  FolderPlus, Folder, X as XIcon, Plus,
} from "lucide-react";
import api, { apiErr, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common/Loader";
import { StatusBadge, EmptyState } from "@/components/dashboard/ui";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { Lightbox } from "@/components/gallery/Lightbox";
import { QRCard } from "@/components/dashboard/QRCard";

const TABS = [{ id: "photos", label: "Photos", icon: ImageIcon }, { id: "qr", label: "QR Code", icon: QrCode }, { id: "selections", label: "Selections", icon: CheckSquare }];

export default function EventDetail() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "photos";
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [sel, setSel] = useState(null);
  const [lightIdx, setLightIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [upMsg, setUpMsg] = useState("");
  const [drag, setDrag] = useState(false);
  const [activeFolder, setActiveFolder] = useState("All");
  const [addingFolder, setAddingFolder] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const fileRef = useRef(null);
  const folderRef = useRef(null);

  const folders = event?.folders?.length ? event.folders : ["General"];
  const uploadTarget = activeFolder === "All" ? "General" : activeFolder;

  const link = `${window.location.origin}/event/${eventId}`;

  const loadEvent = useCallback(() => api.get(`/events/${eventId}`).then((r) => setEvent(r.data.event)), [eventId]);
  const loadPhotos = useCallback(() => api.get(`/events/${eventId}/photos`).then((r) => setPhotos(r.data.photos)), [eventId]);
  const loadSel = useCallback(() => api.get(`/events/${eventId}/selections`).then((r) => setSel(r.data)), [eventId]);

  useEffect(() => { loadEvent().catch(() => {}); loadPhotos().catch(() => {}); }, [loadEvent, loadPhotos]);
  useEffect(() => { if (tab === "selections") loadSel().catch(() => {}); }, [tab, loadSel]);

  const setTab = (id) => setParams({ tab: id });

  const uploadOne = async (blob, name, folder) => {
    const CHUNK = 4 * 1024 * 1024; // 4MB — safely under ingress body limit
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const chunks = Math.max(1, Math.ceil(blob.size / CHUNK));
    for (let i = 0; i < chunks; i++) {
      const part = blob.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", i);
      fd.append("chunk", part, name);
      await api.post(`/events/${eventId}/upload-chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }
    const cfd = new FormData();
    cfd.append("upload_id", uploadId);
    cfd.append("filename", name);
    cfd.append("folder", folder);
    const res = await api.post(`/events/${eventId}/upload-complete`, cfd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data?.uploaded ?? 0;
  };

  const IMG_RE = /\.(jpe?g|png|webp|gif)$/i;

  const compressImage = (blob) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 2048;
        let { width, height } = img;
        const m = Math.max(width, height);
        if (m > MAX) { const s = MAX / m; width = Math.round(width * s); height = Math.round(height * s); }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((out) => resolve(out && out.size ? out : blob), "image/jpeg", 0.82);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(blob); };
      img.src = url;
    });

  const doUpload = async (files) => {
    if (!files?.length) return;
    const folder = uploadTarget;
    setUploading(true); setProgress(0); setUpMsg("Scanning files…");
    const readers = [];
    try {
      // Build a LAZY source list (blobs are read only when a worker picks them up),
      // so selecting a whole folder of thousands of photos never blows up memory.
      const sources = [];
      for (const f of Array.from(files)) {
        const isZip = /\.zip$/i.test(f.name) || (f.type || "").includes("zip");
        if (isZip) {
          // Streaming ZIP reader: reads each entry from disk on demand (no full
          // in-memory load), so even a 50GB zip opens and uploads part-by-part.
          const reader = new ZipReader(new BlobReader(f));
          readers.push(reader);
          const entries = await reader.getEntries();
          for (const e of entries) {
            if (!e.directory && IMG_RE.test(e.filename) && !e.filename.startsWith("__MACOSX")) {
              sources.push({ name: e.filename.split("/").pop(), getBlob: () => e.getData(new BlobWriter()) });
            }
          }
        } else if (IMG_RE.test(f.name) || (f.type || "").startsWith("image/")) {
          sources.push({ name: (f.name.split("/").pop() || f.name), getBlob: async () => f });
        }
      }
      const total = sources.length;
      if (!total) { toast.error("No supported images found (JPG, PNG, WEBP)"); return; }

      const st = { done: 0, ok: 0, failed: 0 };
      let idx = 0;
      const worker = async () => {
        while (true) {
          const i = idx++;
          if (i >= total) break;
          const s = sources[i];
          const jpgName = s.name.replace(/\.[^.]+$/, "") + ".jpg";
          try {
            const raw = await s.getBlob();
            let blob = raw;
            try { blob = await compressImage(raw); } catch { /* use original */ }
            let attempt = 0, uploaded = 0;
            while (attempt < 2) {
              try { uploaded = await uploadOne(blob, jpgName, folder); break; }
              catch { attempt++; }
            }
            if (uploaded) st.ok += uploaded; else st.failed++;
          } catch { st.failed++; }
          st.done++;
          setProgress(Math.round((st.done / total) * 100));
          setUpMsg(`${st.done} / ${total} uploaded${st.failed ? ` · ${st.failed} failed` : ""}`);
        }
      };
      const N = Math.min(4, total); // small parallelism: fast but memory-safe
      await Promise.all(Array.from({ length: N }, () => worker()));

      toast[st.failed && !st.ok ? "error" : "success"](
        `${st.ok} photo${st.ok !== 1 ? "s" : ""} uploaded to "${folder}"${st.failed ? ` · ${st.failed} failed` : ""}`
      );
      await loadPhotos(); await loadEvent();
    } catch (e) { toast.error(apiErr(e)); }
    finally {
      for (const r of readers) { try { await r.close(); } catch { /* noop */ } }
      setUploading(false); setProgress(0); setUpMsg("");
    }
  };

  const addFolder = async () => {
    const name = newFolder.trim();
    if (!name) { setAddingFolder(false); return; }
    try {
      await api.post(`/events/${eventId}/folders`, { name });
      setNewFolder(""); setAddingFolder(false);
      await loadEvent();
      setActiveFolder(name);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removeFolder = async (name) => {
    if (name === "General") return;
    if (!window.confirm(`Delete folder "${name}"? Its photos move to General.`)) return;
    try {
      await api.delete(`/events/${eventId}/folders/${encodeURIComponent(name)}`);
      if (activeFolder === name) setActiveFolder("All");
      await loadEvent(); await loadPhotos();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delPhoto = async (p) => {
    setPhotos((prev) => prev.filter((x) => x.id !== p.id));
    await api.delete(`/photos/${p.id}`).catch(() => {});
    loadEvent();
  };

  const act = async (path, ok) => { try { await api.post(`/events/${eventId}/${path}`); toast.success(ok); loadSel(); loadEvent(); } catch (e) { toast.error(apiErr(e)); } };

  if (!event) return <Loader label="Loading event" />;

  const shown = activeFolder === "All" ? photos : photos.filter((p) => (p.category || "General") === activeFolder);

  return (
    <div>
      <Link to="/app/events" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> All events</Link>

      <div className="relative rounded-2xl overflow-hidden border border-border mb-6">
        <div className="aspect-[21/8] bg-secondary">
          {event.cover_image && <img src={mediaUrl(event.cover_image)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </div>
        <div className="absolute bottom-4 left-5 right-5 flex flex-col sm:flex-row sm:items-end justify-between gap-3 keep-white">
          <div>
            <div className="flex items-center gap-2 mb-1"><span className="text-[11px] glass-dark rounded-full px-2.5 py-1 text-gold">{event.event_type}</span><StatusBadge status={event.selection_status} /></div>
            <h1 className="font-serif text-3xl sm:text-4xl text-white font-light">{event.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-white/70 mt-1">
              <span>Client: {event.client_name}</span>
              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{event.event_date}</span>
              {event.venue && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.venue}</span>}
            </div>
          </div>
          <a href={link} target="_blank" rel="noreferrer" data-testid="open-client-gallery" className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-2 text-sm text-white hover:border-gold/40 self-start">
            <ExternalLink className="w-4 h-4" /> Open client gallery
          </a>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-muted-foreground hover:text-white"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "photos" && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            data-testid="upload-dropzone"
            className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${drag ? "border-gold bg-gold/5" : "border-border hover:border-gold/40"}`}>
            <input ref={fileRef} type="file" multiple accept="image/*,.zip,application/zip" className="hidden" data-testid="upload-input" onChange={(e) => doUpload(e.target.files)} />
            <input ref={folderRef} type="file" multiple webkitdirectory="" directory="" className="hidden" data-testid="upload-folder-input" onChange={(e) => doUpload(e.target.files)} />
            {uploading ? (
              <div className="max-w-md mx-auto">
                <Loader2 className="w-7 h-7 text-gold animate-spin mx-auto mb-3" />
                <div className="text-white text-sm">{upMsg || "Uploading & running face indexing…"}</div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-3"><div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} /></div>
                <div className="text-xs text-muted-foreground mt-2">{progress}%</div>
                <div className="text-[11px] text-muted-foreground mt-2">Please keep this tab open until it finishes.</div>
              </div>
            ) : (
              <>
                <div className="w-14 h-14 rounded-2xl bg-gold/15 flex items-center justify-center mx-auto mb-4"><Upload className="w-6 h-6 text-gold" /></div>
                <div className="text-white font-medium">Drag & drop photos or a ZIP file here</div>
                <div className="text-sm text-muted-foreground mt-1">Uploads go to folder <span className="text-gold font-medium">"{uploadTarget}"</span>. Photos are auto-compressed in your browser, so even a huge shoot (thousands of photos) uploads fast.</div>
                <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                  <button data-testid="select-photos-btn" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} className="inline-flex items-center gap-2 rounded-full bg-gold text-black px-4 py-2 text-sm font-medium hover:bg-gold/90">
                    <ImageIcon className="w-4 h-4" /> Select photos / ZIP
                  </button>
                  <button data-testid="select-folder-btn" onClick={(e) => { e.stopPropagation(); folderRef.current?.click(); }} className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm text-white hover:border-gold/40">
                    <Folder className="w-4 h-4" /> Select entire folder
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">For very large shoots (up to ~50GB), use <span className="text-gold">Select entire folder</span> or a big <span className="text-gold">.zip</span> — both are streamed part-by-part from disk, so nothing overloads your browser.</div>
              </>
            )}
          </div>

          <div className="mt-6 mb-4">
            <div className="flex items-center gap-2 mb-2"><Folder className="w-4 h-4 text-gold" /><span className="text-sm text-white font-medium">Function folders</span></div>
            <div className="flex gap-2 flex-wrap items-center">
              {["All", ...folders].map((f) => {
                const count = f === "All" ? photos.length : photos.filter((p) => (p.category || "General") === f).length;
                return (
                  <span key={f} className={`group inline-flex items-center rounded-full text-sm transition-colors ${activeFolder === f ? "bg-gold text-black" : "glass text-white hover:border-gold/40"}`}>
                    <button data-testid={`folder-${f}`} onClick={() => setActiveFolder(f)} className="pl-4 pr-2 py-1.5">{f} <span className="opacity-70">({count})</span></button>
                    {f !== "All" && f !== "General" && (
                      <button data-testid={`folder-del-${f}`} onClick={() => removeFolder(f)} className="pr-3 pl-0.5 py-1.5 opacity-60 hover:opacity-100"><XIcon className="w-3.5 h-3.5" /></button>
                    )}
                  </span>
                );
              })}
              {addingFolder ? (
                <span className="inline-flex items-center gap-1 glass rounded-full pl-3 pr-1 py-1">
                  <input autoFocus data-testid="folder-name-input" value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addFolder(); if (e.key === "Escape") { setAddingFolder(false); setNewFolder(""); } }}
                    placeholder="Mehndi, Sangeet…" className="bg-transparent text-sm text-white outline-none w-32 placeholder:text-muted-foreground" />
                  <button data-testid="folder-add-confirm" onClick={addFolder} className="w-7 h-7 rounded-full bg-gold text-black flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                </span>
              ) : (
                <button data-testid="folder-add-btn" onClick={() => setAddingFolder(true)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm border border-dashed border-gold/40 text-gold hover:bg-gold/10">
                  <FolderPlus className="w-4 h-4" /> New folder
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground"><span className="text-white font-medium">{shown.length}</span> photos{activeFolder !== "All" ? ` in "${activeFolder}"` : ""} · <span className="text-gold">{shown.filter((p) => p.has_face).length}</span> face-indexed</div>
          </div>

          {shown.length === 0 ? (
            <EmptyState icon={ImageIcon} title="No photos here yet" desc={activeFolder === "All" ? "Upload photos to build this client's gallery." : `Upload photos to the "${activeFolder}" folder.`} />
          ) : (
            <PhotoMasonry photos={shown} watermark={event.watermark} deletable onDelete={delPhoto} onOpen={setLightIdx} />
          )}

          <Lightbox photos={shown} index={lightIdx} onClose={() => setLightIdx(null)} setIndex={setLightIdx} downloadable watermark={event.watermark} />
        </div>
      )}

      {tab === "qr" && (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <QRCard event={event} link={link} studioName={user?.business_name || user?.name} />
          <div className="rounded-2xl border border-border bg-black/40 p-6">
            <h3 className="text-white font-medium">Share with your client</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">Print the QR card and place it at the venue, or send the secure link over WhatsApp. Clients scan, upload a selfie, and instantly discover their photos.</p>
            <div className="mt-5 space-y-3">
              {[["1", "Client scans the QR or opens the link"], ["2", "Uploads a selfie (with consent)"], ["3", "AI finds every photo they appear in"], ["4", "They select favourites & submit their album"]].map(([n, t]) => (
                <div key={n} className="flex items-center gap-3"><div className="w-7 h-7 rounded-full bg-gold/15 text-gold text-xs flex items-center justify-center shrink-0">{n}</div><span className="text-sm text-secondary-foreground">{t}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "selections" && (sel ? (
        <SelectionsView sel={sel} onOpen={setLightIdx} onAct={act} eventId={eventId} reload={loadSel} />
      ) : <Loader label="Loading selections" />)}
    </div>
  );
}

function SelectionsView({ sel, onAct }) {
  const [sub, setSub] = useState("selected");
  const [lightIdx, setLightIdx] = useState(null);
  const groups = { all: sel.all, selected: sel.selected, not_selected: sel.not_selected, favorites: sel.favorites };
  const list = groups[sub] || [];
  const pct = sel.all.length ? Math.round((sel.selected.length / sel.all.length) * 100) : 0;

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["All Photos", sel.all.length], ["Selected", sel.selected.length], ["Not Selected", sel.not_selected.length], ["Favourites", sel.favorites.length]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-border bg-black/40 p-4"><div className="text-xs text-muted-foreground uppercase tracking-wider">{l}</div><div className="font-serif text-2xl text-white mt-1">{v}</div></div>
        ))}
      </div>

      <div className="rounded-2xl border border-gold/20 bg-gradient-to-r from-gold/5 to-transparent p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2"><span className="text-white font-medium">Client selection</span><StatusBadge status={sel.event.selection_status} /></div>
          <div className="text-sm text-muted-foreground mt-1">{sel.selected.length} of {sel.all.length} photos selected · {pct}% complete</div>
          {sel.submission?.comments && <div className="mt-2 flex items-start gap-2 text-sm text-secondary-foreground"><MessageSquare className="w-4 h-4 text-gold mt-0.5 shrink-0" />"{sel.submission.comments}"</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="sel-approve" onClick={() => onAct("selections/approve", "Selection approved")} className="inline-flex items-center gap-2 bg-gold text-black text-sm rounded-full px-4 py-2 hover:bg-gold-light"><CheckCircle2 className="w-4 h-4" /> Approve</button>
          <button data-testid="sel-changes" onClick={() => onAct("selections/request-changes", "Change request sent")} className="inline-flex items-center gap-2 glass text-white text-sm rounded-full px-4 py-2 hover:border-gold/40"><XCircle className="w-4 h-4" /> Request changes</button>
          <button data-testid="sel-reminder" onClick={() => onAct("reminder", "Reminder sent to client")} className="inline-flex items-center gap-2 glass text-white text-sm rounded-full px-4 py-2 hover:border-gold/40"><Bell className="w-4 h-4" /> Remind</button>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {[["selected", "Selected"], ["favorites", "Favourites"], ["not_selected", "Not Selected"], ["all", "All Photos"]].map(([id, l]) => (
          <button key={id} data-testid={`selsub-${id}`} onClick={() => setSub(id)} className={`text-sm rounded-full px-4 py-1.5 transition-colors ${sub === id ? "bg-gold text-black" : "glass text-white hover:border-gold/40"}`}>{l} ({groups[id].length})</button>
        ))}
      </div>

      {list.length === 0 ? <EmptyState icon={Heart} title="Nothing here yet" desc="Photos will appear once the client makes selections." />
        : <PhotoMasonry photos={list} watermark={sel.event.watermark} onOpen={setLightIdx} />}
      <Lightbox photos={list} index={lightIdx} onClose={() => setLightIdx(null)} setIndex={setLightIdx} downloadable watermark={sel.event.watermark} />
    </div>
  );
}
